import { useTableTemplateStore } from "../../store/tableTemplateStore";
import type { TableTemplate } from "../../models/ManualAuthoring";

export default function TemplateMetadata() {
  const schema = useTableTemplateStore((s) => s.editorSchema);
  const selectedTemplate = useTableTemplateStore((s) => s.selectedTemplate);
  const lockState = useTableTemplateStore((s) => s.lockState);
  const isSaving = useTableTemplateStore((s) => s.isSaving);
  const updateSchema = useTableTemplateStore((s) => s.updateSchema);

  if (!schema) return null;

  const isReadOnly = !!lockState?.isLocked || isSaving;

  const setField = <K extends keyof TableTemplate>(field: K, value: TableTemplate[K]) => {
    updateSchema({
      ...schema,
      [field]: value,
    });
  };

  const templateName = schema.templateName ?? selectedTemplate?.templateName ?? "";
  const displayTitle = schema.displayTitle ?? selectedTemplate?.displayTitle ?? "";
  const description = schema.description ?? "";
  const defaultRows = schema.defaultRows;
  const width = schema.width ?? selectedTemplate?.width ?? "100%";
  const alignment = schema.alignment ?? selectedTemplate?.alignment ?? "left";
  const headerRepeat =
    schema.headerRepeat ?? selectedTemplate?.headerRepeat ?? true;
  const keepTogether =
    schema.keepTogether ?? selectedTemplate?.keepTogether ?? false;

  return (
    <section className="metadata-card" aria-label="Table Metadata">
      <div className="metadata-card-header">
        <h3 className="metadata-title">Table Metadata</h3>
        <p className="metadata-help">
          Template-level settings stored in the backend and applied wherever this
          table is inserted later.
        </p>
      </div>

      <div className="metadata-grid">
        <div className="metadata-field metadata-field-full">
          <label htmlFor="template-name">Template Name</label>
          <input
            id="template-name"
            value={templateName}
            onChange={(e) => setField("templateName", e.target.value)}
            disabled={isReadOnly}
          />
        </div>

        <div className="metadata-field metadata-field-full">
          <label htmlFor="template-title">Display Title</label>
          <input
            id="template-title"
            value={displayTitle}
            onChange={(e) => setField("displayTitle", e.target.value)}
            disabled={isReadOnly}
          />
        </div>

        <div className="metadata-field metadata-field-full">
          <label htmlFor="template-description">Description</label>
          <textarea
            id="template-description"
            value={description}
            onChange={(e) => setField("description", e.target.value)}
            disabled={isReadOnly}
            rows={3}
          />
        </div>

        <div className="metadata-field">
          <label htmlFor="template-default-rows">Default Rows</label>
          <input
            id="template-default-rows"
            type="number"
            min={0}
            value={defaultRows ?? ""}
            onChange={(e) => {
              const raw = e.target.value.trim();
              setField("defaultRows", raw ? Number(raw) : undefined);
            }}
            disabled={isReadOnly}
          />
        </div>

        <div className="metadata-field">
          <label htmlFor="template-width">Width</label>
          <input
            id="template-width"
            value={width ?? ""}
            onChange={(e) => setField("width", e.target.value || undefined)}
            disabled={isReadOnly}
          />
        </div>

        <div className="metadata-field metadata-field-full">
          <label htmlFor="template-alignment">Alignment</label>
          <select
            id="template-alignment"
            value={alignment ?? ""}
            onChange={(e) =>
              setField(
                "alignment",
                (e.target.value || undefined) as TableTemplate["alignment"]
              )
            }
            disabled={isReadOnly}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>

      <div className="metadata-switches">
        <label>
          <input
            type="checkbox"
            checked={headerRepeat}
            onChange={(e) => setField("headerRepeat", e.target.checked)}
            disabled={isReadOnly}
          />
          Header Repeat
        </label>

        <label>
          <input
            type="checkbox"
            checked={keepTogether}
            onChange={(e) => setField("keepTogether", e.target.checked)}
            disabled={isReadOnly}
          />
          Keep Together
        </label>
      </div>
    </section>
  );
}
